import { useState } from 'react';
import { cn } from '../../utils/utils';

interface TextareaFieldProps {
    value?: string;
    onChange?: (value: string) => void;
    label: string;
    placeholder?: string;
    rows?: number;
    disabled?: boolean;
    className?: string;
    /** Render the label as a separate block above the textarea rather than
     *  the floating-label-inside pattern. Matches the convention used on
     *  the onboarding form and other newer admin-ui sections. */
    labelAbove?: boolean;
}

const TextareaField = ({
    value,
    onChange,
    label,
    placeholder,
    rows = 4,
    disabled = false,
    className,
    labelAbove = false,
}: TextareaFieldProps) => {
    const [focused, setFocused] = useState(false);
    const isFloating = focused || !!value;

    if (labelAbove) {
        return (
            <div className={cn('flex flex-col gap-1.5', className)}>
                <label className={'text-sm text-foreground/70 font-medium'}>
                    {label}
                </label>
                <textarea
                    className={cn(
                        'py-2 px-4 rounded-xl text-foreground bg-transparent w-full',
                        'border transition-all outline-none resize-none',
                        focused ? 'border-primary' : 'border-foreground',
                        disabled && 'cursor-not-allowed',
                    )}
                    value={value}
                    onChange={(e) => onChange?.(e.target.value)}
                    placeholder={focused ? '' : placeholder}
                    rows={rows}
                    disabled={disabled}
                    onFocus={() => setFocused(true)}
                    onBlur={() => setFocused(false)}
                />
            </div>
        );
    }

    return (
        <div className={cn('relative', className)}>
            <textarea
                className={cn(
                    'py-2 px-4 rounded-xl text-foreground bg-transparent w-full',
                    'border transition-all outline-none resize-none',
                    focused ? 'border-primary' : 'border-foreground',
                    disabled && 'cursor-not-allowed',
                )}
                value={value}
                onChange={(e) => onChange?.(e.target.value)}
                placeholder={isFloating ? placeholder : ''}
                rows={rows}
                disabled={disabled}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
            />
            <label
                className={cn(
                    'absolute left-3 pointer-events-none transition-all duration-200',
                    isFloating
                        ? '-top-2.5 text-xs bg-white px-1 text-foreground'
                        : 'top-2.5 text-sm text-foreground',
                )}
            >
                {label}
            </label>
        </div>
    );
};

export default TextareaField;
